import React, { useState, useEffect, useRef } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SavedJourney, Folder } from "@/utils/storage/types";
import { Pencil, Trash2, FileText, FolderIcon, CheckSquare } from "lucide-react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { deleteJourney, deleteFolder } from "@/utils/storage";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TidyUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedJourneys: SavedJourney[];
  folders: Folder[];
  onDeleteJourney: (id: string) => void;
  onDeleteFolder: (id: string) => void;
  onRenameFolder: (id: string, newName: string) => void;
  onTabChange?: (tab: string) => void;
  initialTab?: "journeys" | "folders";
}

// Popup dialog for folder renaming
const RenameFolderDialog = ({
  open, 
  onOpenChange,
  folder,
  onRename
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: Folder | null;
  onRename: (id: string, newName: string) => void;
}) => {
  const [newName, setNewName] = useState(folder?.name || "");

  // Reset name when dialog opens with new folder
  React.useEffect(() => {
    if (open && folder) {
      setNewName(folder.name);
    }
  }, [open, folder]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (folder && newName.trim()) {
      onRename(folder.id, newName.trim());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rename Folder</DialogTitle>
          <DialogDescription>
            Enter a new name for the folder.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="folderName">Folder name</Label>
              <Input
                id="folderName"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter folder name"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={!newName.trim() || newName === folder?.name}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Helper component for displaying truncated text with tooltip
const TruncatedText = ({ text, className = "" }: { text: string, className?: string }) => (
  <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`truncate max-w-full ${className}`}>
          {text}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-[250px] break-words">
        <p className="text-xs">{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const TidyUpDialog: React.FC<TidyUpDialogProps> = ({
  open,
  onOpenChange,
  savedJourneys,
  folders,
  onDeleteJourney,
  onDeleteFolder,
  onRenameFolder,
  onTabChange,
  initialTab = "journeys",
}) => {
  // Use initialTab if provided, otherwise default to "journeys"
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  
  // Selection state
  const [selectedJourneys, setSelectedJourneys] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  
  // Update active tab when dialog opens with initialTab
  useEffect(() => {
    if (open && initialTab) {
      setActiveTab(initialTab);
    }
  }, [open, initialTab]);
  
  // Clear selections when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setRenameDialogOpen(false);
      setSelectedJourneys([]);
      setSelectedFolders([]);
    }
  }, [open]);

  // Handle tab changes and notify parent
  const handleTabChange = (value: string) => {
    // Update local state
    setActiveTab(value);
    
    // Clear selections when changing tabs
    setSelectedJourneys([]);
    setSelectedFolders([]);
    
    // Notify parent if callback provided
    if (onTabChange) {
      onTabChange(value);
    }
  };

  const handleOpenRenameDialog = (folder: Folder) => {
    setSelectedFolder(folder);
    setRenameDialogOpen(true);
  };

  // Selection handlers
  const toggleJourneySelection = (id: string) => {
    setSelectedJourneys(prev => 
      prev.includes(id) 
        ? prev.filter(journeyId => journeyId !== id)
        : [...prev, id]
    );
  };

  const toggleFolderSelection = (id: string) => {
    setSelectedFolders(prev => 
      prev.includes(id) 
        ? prev.filter(folderId => folderId !== id)
        : [...prev, id]
    );
  };

  // Select all items
  const selectAllJourneys = () => {
    if (selectedJourneys.length === savedJourneys.length) {
      setSelectedJourneys([]);
    } else {
      setSelectedJourneys(savedJourneys.map(j => j.id));
    }
  };

  const selectAllFolders = () => {
    if (selectedFolders.length === folders.length) {
      setSelectedFolders([]);
    } else {
      setSelectedFolders(folders.map(f => f.id));
    }
  };

  // Bulk deletion handlers
  const handleDeleteSelectedJourneys = () => {
    if (selectedJourneys.length === 0) {
      toast.info("No journey maps selected");
      return;
    }
    
    // Delete all selected journeys
    selectedJourneys.forEach(id => {
      onDeleteJourney(id);
    });
    
    // Show success message
    toast.success(`Deleted ${selectedJourneys.length} journey map${selectedJourneys.length !== 1 ? 's' : ''}`);
    
    // Clear selection
    setSelectedJourneys([]);
    
    // Close dialog after bulk operation
    onOpenChange(false);
  };

  const handleDeleteSelectedFolders = () => {
    if (selectedFolders.length === 0) {
      toast.info("No folders selected");
      return;
    }
    
    // Delete all selected folders
    selectedFolders.forEach(id => {
      onDeleteFolder(id);
    });
    
    // Show success message
    toast.success(`Deleted ${selectedFolders.length} folder${selectedFolders.length !== 1 ? 's' : ''}`);
    
    // Clear selection
    setSelectedFolders([]);
    
    // Close dialog after bulk operation
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] w-[calc(100vw-24px)] max-w-full max-h-[90vh] overflow-hidden p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle>Tidy Up Journey Maps</DialogTitle>
            <DialogDescription>
              Manage your saved journey maps and folders
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col overflow-hidden h-full">
            <div className="flex justify-center mb-3">
              <div className="grid grid-cols-2 w-full rounded-lg overflow-hidden border">
                <button 
                  className={`px-3 py-1.5 text-sm text-center ${activeTab === 'journeys' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted hover:bg-muted/80'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabChange('journeys');
                  }}
                >
                  Journey Maps
                </button>
                <button 
                  className={`px-3 py-1.5 text-sm text-center ${activeTab === 'folders' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted hover:bg-muted/80'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTabChange('folders');
                  }}
                >
                  Folders
                </button>
              </div>
            </div>
            
            {/* Journey Maps Tab Content */}
            {activeTab === 'journeys' && (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Selection controls */}
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    {selectedJourneys.length > 0 ? (
                      <span>{selectedJourneys.length} selected</span>
                    ) : (
                      <span>{savedJourneys.length} journey maps</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-7 text-xs"
                      onClick={selectAllJourneys}
                      disabled={savedJourneys.length === 0}
                    >
                      {selectedJourneys.length === savedJourneys.length && savedJourneys.length > 0
                        ? "Deselect All" 
                        : "Select All"}
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleDeleteSelectedJourneys}
                      disabled={selectedJourneys.length === 0}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete Selected
                    </Button>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto border rounded-md min-h-0" style={{ maxHeight: 'calc(70vh - 200px)' }}>
                  {savedJourneys.length === 0 ? (
                    <div className="text-center text-muted-foreground py-6">
                      No saved journey maps
                    </div>
                  ) : (
                    <div className="divide-y">
                      {savedJourneys.map((journey) => {
                        const folder = journey.folderId 
                          ? folders.find(f => f.id === journey.folderId) 
                          : null;
                        const isSelected = selectedJourneys.includes(journey.id);
                        
                        return (
                          <div 
                            key={journey.id}
                            className={`flex items-center p-3 ${isSelected ? 'bg-muted/50' : ''}`}
                          >
                            <div className="mr-2 flex-shrink-0">
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={() => toggleJourneySelection(journey.id)}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium flex items-center min-w-0">
                                <FileText className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <TruncatedText text={journey.name} />
                              </div>
                              <div className="text-xs text-muted-foreground flex min-w-0">
                                <span className="mr-2 flex-shrink-0">
                                  {format(new Date(journey.timestamp), "MMM d, yyyy h:mm a")}
                                </span>
                                {folder && (
                                  <span className="flex items-center min-w-0">
                                    <FolderIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                    <TruncatedText text={folder.name} />
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Folders Tab Content */}
            {activeTab === 'folders' && (
              <div className="flex flex-col flex-1 min-h-0">
                {/* Selection controls */}
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    {selectedFolders.length > 0 ? (
                      <span>{selectedFolders.length} selected</span>
                    ) : (
                      <span>{folders.length} folders</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-7 text-xs"
                      onClick={selectAllFolders}
                      disabled={folders.length === 0}
                    >
                      {selectedFolders.length === folders.length && folders.length > 0
                        ? "Deselect All" 
                        : "Select All"}
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleDeleteSelectedFolders}
                      disabled={selectedFolders.length === 0}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete Selected
                    </Button>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto border rounded-md min-h-0" style={{ maxHeight: 'calc(70vh - 200px)' }}>
                  {folders.length === 0 ? (
                    <div className="text-center text-muted-foreground py-6">
                      No folders created
                    </div>
                  ) : (
                    <div className="divide-y">
                      {folders.map((folder) => {
                        const folderJourneys = savedJourneys.filter(j => j.folderId === folder.id);
                        const isSelected = selectedFolders.includes(folder.id);
                        
                        return (
                          <div 
                            key={folder.id}
                            className={`flex items-center p-3 ${isSelected ? 'bg-muted/50' : ''}`}
                          >
                            <div className="mr-2 flex-shrink-0">
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={() => toggleFolderSelection(folder.id)}
                              />
                            </div>
                            <div className="flex-1 flex items-center min-w-0">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium flex items-center min-w-0">
                                  <FolderIcon className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <TruncatedText text={folder.name} />
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {folderJourneys.length} {folderJourneys.length === 1 ? 'map' : 'maps'}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="ml-auto hover:bg-blue-50 hover:text-blue-600 flex-shrink-0"
                                onClick={() => handleOpenRenameDialog(folder)}
                              >
                                <Pencil className="h-3 w-3" />
                                <span className="sr-only">Rename</span>
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-3 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Separate dialog for renaming folders */}
      <RenameFolderDialog
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        folder={selectedFolder}
        onRename={onRenameFolder}
      />
    </>
  );
};

export default TidyUpDialog; 